import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import PolicySection from './components/PolicySection';
import TableOfContents from './components/TableOfContents';
import ContactCard from './components/ContactCard';
import DataRightsCard from './components/DataRightsCard';
import SecurityMeasures from './components/SecurityMeasures';

const PrivacyPolicy = () => {
  const [activeSection, setActiveSection] = useState('');

  const sections = [
    { id: 'overview', title: 'Privacy Overview' },
    { id: 'information-collection', title: 'Information We Collect' },
    { id: 'usage', title: 'How We Use Information' },
    { id: 'sharing', title: 'Information Sharing' },
    { id: 'cookies', title: 'Cookies & Tracking' },
    { id: 'third-party', title: 'Third-Party Services' },
    { id: 'security', title: 'Data Security' },
    { id: 'rights', title: 'Your Rights' },
    { id: 'retention', title: 'Data Retention' },
    { id: 'international', title: 'International Transfers' },
    { id: 'children', title: 'Children\'s Privacy' },
    { id: 'updates', title: 'Policy Updates' },
    { id: 'contact', title: 'Contact Information' }
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 100;
      const elementPosition = element?.getBoundingClientRect()?.top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150;
      
      for (const section of sections) {
        const element = document.getElementById(section?.id);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section?.id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const lastUpdated = new Date()?.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });

  return (
    <>
      <Helmet>
        <title>Privacy Policy - Mava Cold Beverages | Data Protection & Privacy Rights</title>
        <meta name="description" content="Learn how Mava protects your privacy and personal data. Comprehensive privacy policy covering data collection, usage, security measures, and your rights." />
        <meta name="keywords" content="privacy policy, data protection, GDPR compliance, personal information, Mava beverages" />
        <link rel="canonical" href="/privacy-policy" />
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Header Section */}
        <section className="bg-gradient-to-br from-primary/5 via-accent/5 to-background pt-24 pb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <div className="flex items-center justify-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center liquid-shadow">
                  <Icon name="Shield" size={32} color="white" />
                </div>
              </div>
              <h1 className="text-4xl md:text-5xl font-poppins font-bold text-primary mb-4">
                Privacy Policy
              </h1>
              <p className="text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed">
                Your privacy matters to us. Learn how we collect, use, and protect your personal information 
                while delivering the best hydration experience.
              </p>
              <div className="flex items-center justify-center mt-6 space-x-6 text-sm text-text-secondary">
                <div className="flex items-center">
                  <Icon name="Calendar" size={16} className="mr-2 text-accent" />
                  <span>Last Updated: {lastUpdated}</span>
                </div>
                <div className="flex items-center">
                  <Icon name="Clock" size={16} className="mr-2 text-accent" />
                  <span>15 min read</span>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Button variant="outline" size="sm">
                <Icon name="Download" size={16} className="mr-2" />
                Download PDF
              </Button>
              <Button variant="outline" size="sm">
                <Icon name="Printer" size={16} className="mr-2" />
                Print Policy
              </Button>
              <Button variant="default" size="sm" className="bg-action hover:bg-action/90">
                <Icon name="MessageCircle" size={16} className="mr-2" />
                Ask Questions
              </Button>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              {/* Table of Contents - Desktop */}
              <div className="hidden lg:block">
                <TableOfContents 
                  sections={sections} 
                  onSectionClick={scrollToSection}
                />
              </div>

              {/* Main Content */}
              <div className="lg:col-span-3">
                <div className="max-w-4xl">
                  {/* Privacy Overview */}
                  <div id="overview">
                    <PolicySection title="Privacy Overview">
                      <div className="space-y-4">
                        <p>
                          At Mava Cold Beverages, we believe that privacy is a fundamental right. This Privacy Policy 
                          explains how we collect, use, disclose, and safeguard your information when you visit our 
                          website, use our services, or interact with our brand.
                        </p>
                        <p>
                          We are committed to transparency in our data practices and ensuring you have control over 
                          your personal information. This policy applies to all information collected through our 
                          website, mobile applications, customer service interactions, and marketing communications.
                        </p>
                        <div className="bg-accent/10 border-l-4 border-accent p-4 rounded-r-lg">
                          <div className="flex items-start">
                            <Icon name="Info" size={20} className="text-accent mr-3 mt-0.5 flex-shrink-0" />
                            <div>
                              <h4 className="font-semibold text-text-primary mb-1">Key Principle</h4>
                              <p className="text-sm text-text-secondary">
                                We only collect information that helps us provide better products and services. 
                                We never sell your personal data to third parties.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </PolicySection>
                  </div>

                  {/* Information We Collect */}
                  <div id="information-collection">
                    <PolicySection title="Information We Collect">
                      <div className="space-y-6">
                        <div>
                          <h4 className="font-poppins font-semibold text-text-primary mb-3">Personal Information</h4>
                          <ul className="space-y-2 text-sm">
                            <li className="flex items-start">
                              <Icon name="Check" size={16} className="text-accent mr-2 mt-0.5 flex-shrink-0" />
                              <span><strong>Contact Details:</strong> Name, email address, phone number, postal address</span>
                            </li>
                            <li className="flex items-start">
                              <Icon name="Check" size={16} className="text-accent mr-2 mt-0.5 flex-shrink-0" />
                              <span><strong>Account Information:</strong> Username, password, preferences, order history</span>
                            </li>
                            <li className="flex items-start">
                              <Icon name="Check" size={16} className="text-accent mr-2 mt-0.5 flex-shrink-0" />
                              <span><strong>Payment Data:</strong> Billing information, transaction details (processed securely)</span>
                            </li>
                            <li className="flex items-start">
                              <Icon name="Check" size={16} className="text-accent mr-2 mt-0.5 flex-shrink-0" />
                              <span><strong>Communication Records:</strong> Customer service interactions, feedback, reviews</span>
                            </li>
                          </ul>
                        </div>

                        <div>
                          <h4 className="font-poppins font-semibold text-text-primary mb-3">Automatically Collected Information</h4>
                          <ul className="space-y-2 text-sm">
                            <li className="flex items-start">
                              <Icon name="Monitor" size={16} className="text-accent mr-2 mt-0.5 flex-shrink-0" />
                              <span><strong>Device Information:</strong> IP address, browser type, operating system, device identifiers</span>
                            </li>
                            <li className="flex items-start">
                              <Icon name="Activity" size={16} className="text-accent mr-2 mt-0.5 flex-shrink-0" />
                              <span><strong>Usage Data:</strong> Pages visited, time spent, click patterns, referral sources</span>
                            </li>
                            <li className="flex items-start">
                              <Icon name="MapPin" size={16} className="text-accent mr-2 mt-0.5 flex-shrink-0" />
                              <span><strong>Location Data:</strong> General geographic location (with your consent)</span>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </PolicySection>
                  </div>

                  {/* How We Use Information */}
                  <div id="usage">
                    <PolicySection title="How We Use Your Information">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                          <h4 className="font-poppins font-semibold text-primary">Service Delivery</h4>
                          <ul className="space-y-2 text-sm">
                            <li className="flex items-start">
                              <Icon name="Package" size={14} className="text-accent mr-2 mt-1 flex-shrink-0" />
                              <span>Process and fulfill orders</span>
                            </li>
                            <li className="flex items-start">
                              <Icon name="Headphones" size={14} className="text-accent mr-2 mt-1 flex-shrink-0" />
                              <span>Provide customer support</span>
                            </li>
                            <li className="flex items-start">
                              <Icon name="Bell" size={14} className="text-accent mr-2 mt-1 flex-shrink-0" />
                              <span>Send order updates and notifications</span>
                            </li>
                          </ul>
                        </div>
                        <div className="space-y-4">
                          <h4 className="font-poppins font-semibold text-primary">Improvement & Marketing</h4>
                          <ul className="space-y-2 text-sm">
                            <li className="flex items-start">
                              <Icon name="TrendingUp" size={14} className="text-accent mr-2 mt-1 flex-shrink-0" />
                              <span>Analyze usage patterns</span>
                            </li>
                            <li className="flex items-start">
                              <Icon name="Target" size={14} className="text-accent mr-2 mt-1 flex-shrink-0" />
                              <span>Personalize recommendations</span>
                            </li>
                            <li className="flex items-start">
                              <Icon name="Mail" size={14} className="text-accent mr-2 mt-1 flex-shrink-0" />
                              <span>Send marketing communications</span>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </PolicySection>
                  </div>

                  {/* Information Sharing */}
                  <div id="sharing">
                    <PolicySection title="Information Sharing & Disclosure">
                      <div className="space-y-4">
                        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                          <div className="flex items-start">
                            <Icon name="ShieldCheck" size={20} className="text-primary mr-3 mt-0.5 flex-shrink-0" />
                            <div>
                              <h4 className="font-semibold text-primary mb-1">Our Commitment</h4>
                              <p className="text-sm text-text-secondary">
                                We do not sell, rent, or trade your personal information to third parties for their marketing purposes.
                              </p>
                            </div>
                          </div>
                        </div>
                        
                        <p>We may share your information only in the following circumstances:</p>
                        
                        <div className="space-y-3">
                          <div className="flex items-start">
                            <Icon name="Users" size={16} className="text-accent mr-3 mt-1 flex-shrink-0" />
                            <div>
                              <strong className="text-text-primary">Service Providers:</strong>
                              <span className="text-text-secondary text-sm ml-1">
                                Trusted partners who help us operate our business (payment processors, shipping companies, analytics providers)
                              </span>
                            </div>
                          </div>
                          <div className="flex items-start">
                            <Icon name="Scale" size={16} className="text-accent mr-3 mt-1 flex-shrink-0" />
                            <div>
                              <strong className="text-text-primary">Legal Requirements:</strong>
                              <span className="text-text-secondary text-sm ml-1">
                                When required by law, court order, or to protect our rights and safety
                              </span>
                            </div>
                          </div>
                          <div className="flex items-start">
                            <Icon name="Building" size={16} className="text-accent mr-3 mt-1 flex-shrink-0" />
                            <div>
                              <strong className="text-text-primary">Business Transfers:</strong>
                              <span className="text-text-secondary text-sm ml-1">
                                In connection with mergers, acquisitions, or asset sales (with notice to you)
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </PolicySection>
                  </div>

                  {/* Cookies & Tracking */}
                  <div id="cookies">
                    <PolicySection title="Cookies & Tracking Technologies" isExpandable defaultExpanded={false}>
                      <div className="space-y-4">
                        <p>
                          We use cookies and similar technologies to enhance your browsing experience, 
                          analyze site traffic, and understand where our visitors are coming from.
                        </p>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="bg-surface p-4 rounded-lg">
                            <h5 className="font-semibold text-text-primary mb-2 flex items-center">
                              <Icon name="Settings" size={16} className="mr-2 text-primary" />
                              Essential Cookies
                            </h5>
                            <p className="text-xs text-text-secondary">
                              Required for basic site functionality and security. Cannot be disabled.
                            </p>
                          </div>
                          <div className="bg-surface p-4 rounded-lg">
                            <h5 className="font-semibold text-text-primary mb-2 flex items-center">
                              <Icon name="BarChart3" size={16} className="mr-2 text-accent" />
                              Analytics Cookies
                            </h5>
                            <p className="text-xs text-text-secondary">
                              Help us understand how visitors interact with our website.
                            </p>
                          </div>
                          <div className="bg-surface p-4 rounded-lg">
                            <h5 className="font-semibold text-text-primary mb-2 flex items-center">
                              <Icon name="Target" size={16} className="mr-2 text-action" />
                              Marketing Cookies
                            </h5>
                            <p className="text-xs text-text-secondary">
                              Used to deliver relevant advertisements and track campaign performance.
                            </p>
                          </div>
                        </div>
                        
                        <div className="bg-muted p-4 rounded-lg">
                          <h5 className="font-semibold text-text-primary mb-2">Cookie Management</h5>
                          <p className="text-sm text-text-secondary mb-3">
                            You can control cookies through your browser settings. Note that disabling certain 
                            cookies may affect site functionality.
                          </p>
                          <Button variant="outline" size="sm">
                            <Icon name="Settings" size={16} className="mr-2" />
                            Manage Cookie Preferences
                          </Button>
                        </div>
                      </div>
                    </PolicySection>
                  </div>

                  {/* Third-Party Services */}
                  <div id="third-party">
                    <PolicySection title="Third-Party Services" isExpandable defaultExpanded={false}>
                      <div className="space-y-6">
                        <p>
                          We integrate with trusted third-party services to enhance our platform capabilities. 
                          Each service has its own privacy policy governing the use of your information.
                        </p>
                        
                        <div className="space-y-4">
                          <div className="border border-border rounded-lg p-4">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center">
                                <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center mr-3">
                                  <Icon name="Database" size={16} className="text-primary" />
                                </div>
                                <div>
                                  <h5 className="font-semibold text-text-primary">Zoho CRM</h5>
                                  <p className="text-xs text-text-secondary">Customer relationship management</p>
                                </div>
                              </div>
                              <Button variant="ghost" size="sm">
                                <Icon name="ExternalLink" size={14} />
                              </Button>
                            </div>
                            <p className="text-sm text-text-secondary">
                              Manages customer inquiries, order processing, and communication history. 
                              Data is encrypted and stored in compliance with international standards.
                            </p>
                          </div>
                          
                          <div className="border border-border rounded-lg p-4">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center">
                                <div className="w-8 h-8 bg-accent/10 rounded-lg flex items-center justify-center mr-3">
                                  <Icon name="BarChart3" size={16} className="text-accent" />
                                </div>
                                <div>
                                  <h5 className="font-semibold text-text-primary">Google Analytics 4</h5>
                                  <p className="text-xs text-text-secondary">Website analytics and insights</p>
                                </div>
                              </div>
                              <Button variant="ghost" size="sm">
                                <Icon name="ExternalLink" size={14} />
                              </Button>
                            </div>
                            <p className="text-sm text-text-secondary">
                              Provides insights into website usage, user behavior, and performance metrics. 
                              Data is anonymized and aggregated for analysis.
                            </p>
                          </div>
                          
                          <div className="border border-border rounded-lg p-4">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center">
                                <div className="w-8 h-8 bg-action/10 rounded-lg flex items-center justify-center mr-3">
                                  <Icon name="CreditCard" size={16} className="text-action" />
                                </div>
                                <div>
                                  <h5 className="font-semibold text-text-primary">Payment Processors</h5>
                                  <p className="text-xs text-text-secondary">Secure payment processing</p>
                                </div>
                              </div>
                              <Button variant="ghost" size="sm">
                                <Icon name="ExternalLink" size={14} />
                              </Button>
                            </div>
                            <p className="text-sm text-text-secondary">
                              Handles payment transactions securely. We do not store complete payment card details 
                              on our servers - all processing is PCI DSS compliant.
                            </p>
                          </div>
                        </div>
                      </div>
                    </PolicySection>
                  </div>

                  {/* Data Security */}
                  <div id="security">
                    <PolicySection title="Data Security Measures">
                      <SecurityMeasures />
                    </PolicySection>
                  </div>

                  {/* Your Rights */}
                  <div id="rights">
                    <PolicySection title="Your Privacy Rights">
                      <DataRightsCard />
                    </PolicySection>
                  </div>

                  {/* Data Retention */}
                  <div id="retention">
                    <PolicySection title="Data Retention Policy" isExpandable defaultExpanded={false}>
                      <div className="space-y-4">
                        <p>
                          We retain your personal information only as long as necessary to fulfill the purposes 
                          outlined in this privacy policy, comply with legal obligations, and resolve disputes.
                        </p>
                        
                        <div className="space-y-3">
                          <div className="flex items-start">
                            <Icon name="User" size={16} className="text-accent mr-3 mt-1 flex-shrink-0" />
                            <div>
                              <strong className="text-text-primary">Account Information:</strong>
                              <span className="text-text-secondary text-sm ml-1">
                                Retained while your account is active and for 3 years after closure
                              </span>
                            </div>
                          </div>
                          <div className="flex items-start">
                            <Icon name="ShoppingCart" size={16} className="text-accent mr-3 mt-1 flex-shrink-0" />
                            <div>
                              <strong className="text-text-primary">Transaction Records:</strong>
                              <span className="text-text-secondary text-sm ml-1">
                                Kept for 7 years for accounting and legal compliance purposes
                              </span>
                            </div>
                          </div>
                          <div className="flex items-start">
                            <Icon name="MessageSquare" size={16} className="text-accent mr-3 mt-1 flex-shrink-0" />
                            <div>
                              <strong className="text-text-primary">Communication Records:</strong>
                              <span className="text-text-secondary text-sm ml-1">
                                Maintained for 2 years to improve customer service quality
                              </span>
                            </div>
                          </div>
                          <div className="flex items-start">
                            <Icon name="Activity" size={16} className="text-accent mr-3 mt-1 flex-shrink-0" />
                            <div>
                              <strong className="text-text-primary">Analytics Data:</strong>
                              <span className="text-text-secondary text-sm ml-1">
                                Anonymized data retained for 26 months for business insights
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="bg-muted p-4 rounded-lg">
                          <h5 className="font-semibold text-text-primary mb-2">Automatic Deletion</h5>
                          <p className="text-sm text-text-secondary">
                            Our systems automatically delete data when retention periods expire. You can also 
                            request immediate deletion of your data at any time, subject to legal requirements.
                          </p>
                        </div>
                      </div>
                    </PolicySection>
                  </div>

                  {/* International Transfers */}
                  <div id="international">
                    <PolicySection title="International Data Transfers" isExpandable defaultExpanded={false}>
                      <div className="space-y-4">
                        <p>
                          Your information may be transferred to and processed in countries other than India. 
                          We ensure appropriate safeguards are in place to protect your data during such transfers.
                        </p>
                        
                        <div className="bg-surface border border-border rounded-lg p-4">
                          <h5 className="font-semibold text-text-primary mb-3 flex items-center">
                            <Icon name="Globe" size={18} className="mr-2 text-primary" />
                            Transfer Safeguards
                          </h5>
                          <ul className="space-y-2 text-sm">
                            <li className="flex items-start">
                              <Icon name="FileCheck" size={14} className="text-accent mr-2 mt-1 flex-shrink-0" />
                              <span>Standard Contractual Clauses (SCCs) with all international partners</span>
                            </li>
                            <li className="flex items-start">
                              <Icon name="Shield" size={14} className="text-accent mr-2 mt-1 flex-shrink-0" />
                              <span>Adequacy decisions for transfers to approved countries</span>
                            </li>
                            <li className="flex items-start">
                              <Icon name="Lock" size={14} className="text-accent mr-2 mt-1 flex-shrink-0" />
                              <span>End-to-end encryption for all data in transit</span>
                            </li>
                            <li className="flex items-start">
                              <Icon name="Eye" size={14} className="text-accent mr-2 mt-1 flex-shrink-0" />
                              <span>Regular audits of international processing activities</span>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </PolicySection>
                  </div>

                  {/* Children's Privacy */}
                  <div id="children">
                    <PolicySection title="Children's Privacy Protection" isExpandable defaultExpanded={false}>
                      <div className="space-y-4">
                        <div className="bg-warning/10 border-l-4 border-warning p-4 rounded-r-lg">
                          <div className="flex items-start">
                            <Icon name="AlertTriangle" size={20} className="text-warning mr-3 mt-0.5 flex-shrink-0" />
                            <div>
                              <h4 className="font-semibold text-text-primary mb-1">Age Restriction</h4>
                              <p className="text-sm text-text-secondary">
                                Our services are not intended for children under 13 years of age. We do not 
                                knowingly collect personal information from children under 13.
                              </p>
                            </div>
                          </div>
                        </div>
                        
                        <p>
                          If we learn that we have collected personal information from a child under 13 without 
                          parental consent, we will delete that information as quickly as possible. If you believe 
                          we might have information from or about a child under 13, please contact us immediately.
                        </p>
                        
                        <div className="bg-card border border-border rounded-lg p-4">
                          <h5 className="font-semibold text-text-primary mb-2">Parental Rights</h5>
                          <p className="text-sm text-text-secondary mb-3">
                            Parents and guardians have the right to:
                          </p>
                          <ul className="space-y-1 text-sm text-text-secondary">
                            <li className="flex items-start">
                              <Icon name="Check" size={14} className="text-accent mr-2 mt-1 flex-shrink-0" />
                              <span>Review their child's personal information</span>
                            </li>
                            <li className="flex items-start">
                              <Icon name="Check" size={14} className="text-accent mr-2 mt-1 flex-shrink-0" />
                              <span>Request deletion of their child's information</span>
                            </li>
                            <li className="flex items-start">
                              <Icon name="Check" size={14} className="text-accent mr-2 mt-1 flex-shrink-0" />
                              <span>Refuse further collection or use of their child's information</span>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </PolicySection>
                  </div>

                  {/* Policy Updates */}
                  <div id="updates">
                    <PolicySection title="Policy Updates & Changes" isExpandable defaultExpanded={false}>
                      <div className="space-y-4">
                        <p>
                          We may update this Privacy Policy from time to time to reflect changes in our practices, 
                          technology, legal requirements, or other factors. We will notify you of any material 
                          changes through appropriate channels.
                        </p>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="bg-surface p-4 rounded-lg">
                            <h5 className="font-semibold text-text-primary mb-2 flex items-center">
                              <Icon name="Bell" size={16} className="mr-2 text-primary" />
                              Notification Methods
                            </h5>
                            <ul className="space-y-1 text-sm text-text-secondary">
                              <li>• Email notification to registered users</li>
                              <li>• Website banner announcement</li>
                              <li>• In-app notifications (if applicable)</li>
                              <li>• Updated "Last Modified" date</li>
                            </ul>
                          </div>
                          <div className="bg-surface p-4 rounded-lg">
                            <h5 className="font-semibold text-text-primary mb-2 flex items-center">
                              <Icon name="Calendar" size={16} className="mr-2 text-accent" />
                              Effective Timeline
                            </h5>
                            <ul className="space-y-1 text-sm text-text-secondary">
                              <li>• 30 days notice for material changes</li>
                              <li>• Immediate effect for minor updates</li>
                              <li>• Separate consent for significant changes</li>
                              <li>• Version history maintained</li>
                            </ul>
                          </div>
                        </div>
                        
                        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                          <h5 className="font-semibold text-primary mb-2">Your Continued Use</h5>
                          <p className="text-sm text-text-secondary">
                            By continuing to use our services after policy updates take effect, you acknowledge 
                            that you have read and agree to the revised Privacy Policy. If you disagree with 
                            any changes, you may discontinue using our services.
                          </p>
                        </div>
                      </div>
                    </PolicySection>
                  </div>

                  {/* Contact Information */}
                  <div id="contact">
                    <PolicySection title="Contact & Privacy Support">
                      <ContactCard />
                    </PolicySection>
                  </div>

                  {/* Footer Navigation */}
                  <div className="mt-16 pt-8 border-t border-border">
                    <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
                      <div className="flex items-center space-x-4">
                        <Link 
                          to="/terms-conditions"
                          className="text-sm text-text-secondary hover:text-primary brand-transition flex items-center"
                        >
                          <Icon name="FileText" size={16} className="mr-2" />
                          Terms & Conditions
                        </Link>
                        <Link 
                          to="/contact"
                          className="text-sm text-text-secondary hover:text-primary brand-transition flex items-center"
                        >
                          <Icon name="MessageCircle" size={16} className="mr-2" />
                          Contact Support
                        </Link>
                      </div>
                      <div className="flex items-center space-x-4">
                        <Button variant="outline" size="sm">
                          <Icon name="ArrowUp" size={16} className="mr-2" />
                          Back to Top
                        </Button>
                        <Link to="/homepage">
                          <Button variant="default" size="sm" className="bg-action hover:bg-action/90">
                            <Icon name="Home" size={16} className="mr-2" />
                            Return Home
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default PrivacyPolicy;